import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, ShieldCheck, Mail } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { FormSelect } from '../components/FormSelect';
import { certificationsData } from '../data/certifications';
export function CertificationDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const cert = certificationsData.find(c => c.id === id);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    reason: '',
    message: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  if (!cert) {
    return <Navigate to="/about/certifications" replace data-id="element-419" />;
  }
  const relatedCerts = certificationsData.filter(c => c.id !== id).slice(0, 3);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      id,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would trigger an email to certificates@chilternglobal.com
    // with the form data and the requested certificate ID.
    // Internal instruction: "Reply to requester with the PDF attached."
    setSubmitted(true);
  };
  return <>
      {/* Hero */}
      <section className="bg-navy-900 pt-40 pb-24 border-b border-white/5" data-id="element-420">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-421">
          <div className="border-l-4 border-cg-orange-600 pl-6 max-w-4xl" data-id="element-422">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-423">
              {cert.title}
            </h1>
            <p className="text-lg text-steel-300 leading-relaxed font-light mb-8" data-id="element-424">
              {cert.intro}
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-4 text-sm" data-id="element-425">
              <div className="flex flex-col" data-id="element-426">
                <span className="text-steel-500 font-bold uppercase tracking-wider text-xs mb-1" data-id="element-427">
                  Type
                </span>
                <span className="text-white" data-id="element-428">{cert.type}</span>
              </div>
              <div className="flex flex-col" data-id="element-429">
                <span className="text-steel-500 font-bold uppercase tracking-wider text-xs mb-1" data-id="element-430">
                  {cert.type === 'Membership' ? 'Managed by' : 'Issued by'}
                </span>
                <span className="text-white" data-id="element-431">{cert.issuedBy}</span>
              </div>
              <div className="flex flex-col" data-id="element-432">
                <span className="text-steel-500 font-bold uppercase tracking-wider text-xs mb-1" data-id="element-433">
                  Applies to
                </span>
                <span className="text-white" data-id="element-434">{cert.appliesTo}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumbs items={[{
      label: 'About Us',
      to: '/about'
    }, {
      label: 'Certifications',
      to: '/about/certifications'
    }, {
      label: cert.title
    }]} data-id="element-435" />

      <div className="bg-white py-24" data-id="element-436">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-437">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-438">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16" data-id="element-439">
              {/* Why this matters */}
              <section data-id="element-440">
                <h2 className="text-2xl font-bold text-navy-900 mb-6 flex items-center gap-3" data-id="element-441">
                  <ShieldCheck className="w-6 h-6 text-cg-orange-600" data-id="element-442" />
                  Why this matters
                </h2>
                <ul className="space-y-4" data-id="element-443">
                  {cert.whyItMatters.map((point, index) => <li key={index} className="flex items-start gap-3" data-id="element-444">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-445" />
                      <span className="text-steel-600 leading-relaxed" data-id="element-446">
                        {point}
                      </span>
                    </li>)}
                </ul>
              </section>

              {/* Scope and coverage */}
              <section data-id="element-447">
                <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-448">
                  Scope and coverage
                </h2>
                <p className="text-steel-600 leading-relaxed mb-6" data-id="element-449">
                  {cert.scope.description}
                </p>
                <ul className="space-y-3" data-id="element-450">
                  {cert.scope.items.map((item, index) => <li key={index} className="flex items-center gap-3 text-steel-600" data-id="element-451">
                      <span className="w-1.5 h-1.5 bg-cg-orange-600 rounded-full flex-shrink-0" data-id="element-452" />
                      <span data-id="element-453">{item}</span>
                    </li>)}
                </ul>
              </section>

              {/* Request Form */}
              {cert.hasPdf && <section className="scroll-mt-24" id="request-form" data-id="element-454">
                  <div className="bg-slate-50 p-8 md:p-10 border border-steel-200 rounded-sm" data-id="element-455">
                    <div className="mb-8" data-id="element-456">
                      <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center gap-3" data-id="element-457">
                        <Mail className="w-6 h-6 text-cg-orange-600" data-id="element-458" />
                        Request the certificate (PDF)
                      </h2>
                      <p className="text-steel-600" data-id="element-459">
                        To request a copy of this certificate, complete the form
                        and we will email the PDF.
                      </p>
                    </div>

                    {!submitted ? <form onSubmit={handleSubmit} className="space-y-8" data-id="element-460">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-461">
                          <div data-id="element-462">
                            <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-463">
                              First Name *
                            </label>
                            <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-464" />
                          </div>
                          <div data-id="element-465">
                            <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-466">
                              Last Name *
                            </label>
                            <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-467" />
                          </div>
                          <div data-id="element-468">
                            <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-469">
                              Work Email *
                            </label>
                            <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-470" />
                          </div>
                          <div data-id="element-471">
                            <label htmlFor="company" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-472">
                              Company *
                            </label>
                            <input type="text" id="company" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.company} onChange={handleChange} data-id="element-473" />
                          </div>
                          <div data-id="element-474">
                            <label htmlFor="jobTitle" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-475">
                              Job Title
                            </label>
                            <input type="text" id="jobTitle" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.jobTitle} onChange={handleChange} data-id="element-476" />
                          </div>
                          <div data-id="element-477">
                            <FormSelect id="reason" label="Reason for request" required value={formData.reason} onChange={handleChange} data-id="element-478">
                              <option value="" data-id="element-479">Select reason</option>
                              <option value="Tender" data-id="element-480">Tender</option>
                              <option value="Supplier onboarding" data-id="element-481">
                                Supplier onboarding
                              </option>
                              <option value="Audit" data-id="element-482">Audit</option>
                              <option value="Due diligence" data-id="element-483">
                                Due diligence
                              </option>
                              <option value="Other" data-id="element-484">Other</option>
                            </FormSelect>
                          </div>
                          <div className="md:col-span-2" data-id="element-485">
                            <label htmlFor="message" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-486">
                              Message
                            </label>
                            <textarea id="message" rows={3} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.message} onChange={handleChange} data-id="element-487" />
                          </div>
                        </div>

                        <div className="flex items-start gap-3" data-id="element-488">
                          <input type="checkbox" id="consent" required checked={formData.consent} onChange={handleChange} className="mt-1 w-4 h-4 text-cg-orange-600 border-steel-300 rounded focus:ring-cg-orange-600" data-id="element-489" />
                          <label htmlFor="consent" className="text-sm text-steel-600" data-id="element-490">
                            I confirm this request is for a legitimate business
                            purpose.
                          </label>
                        </div>

                        <div data-id="element-491">
                          <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors" data-id="element-492">
                            Request PDF
                          </button>
                          <div className="mt-4 text-xs text-steel-500 text-center space-y-1" data-id="element-493">
                            <p data-id="element-494">
                              Requests are typically processed during business
                              hours.
                            </p>
                            <p data-id="element-495">
                              We may ask for additional information to verify
                              the request.
                            </p>
                          </div>
                        </div>
                      </form> : <div className="bg-green-50 border border-green-200 p-8 rounded-sm text-center" data-id="element-496">
                        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" data-id="element-497" />
                        <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-498">
                          Thanks — we have received your request.
                        </h3>
                        <p className="text-steel-700" data-id="element-499">
                          If approved, we will email the PDF to{' '}
                          <span className="font-bold" data-id="element-500">{formData.email}</span>.
                        </p>
                      </div>}
                  </div>
                </section>}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-12" data-id="element-501">
              <div data-id="element-502">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 border-b border-steel-200 pb-2" data-id="element-503">
                  Related Certifications
                </h3>
                <div className="space-y-4" data-id="element-504">
                  {relatedCerts.map(related => <Link key={related.id} to={`/about/certifications/${related.id}`} className="block bg-slate-50 border border-steel-200 rounded-sm p-4 hover:border-cg-orange-600 transition-colors group" data-id="element-505">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-cg-orange-600 mb-1 block" data-id="element-506">
                        {related.type}
                      </span>
                      <h4 className="font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-507">
                        {related.title}
                      </h4>
                    </Link>)}
                </div>
              </div>

              <Link to="/about/certifications" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 uppercase tracking-wider hover:text-cg-orange-600 transition-colors" data-id="element-508">
                <ArrowLeft className="w-4 h-4" data-id="element-509" /> Back to Certificates &
                Accreditations
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>;
}